import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaHandshake,
  FaShieldAlt,
  FaHeart,
  FaChartLine,
  FaMapMarkerAlt,
} from "react-icons/fa";

export default function About() {
  // simple fade-up helper
  const fadeUp = (d = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.55, delay: d },
  });

  const sectionTitle = "text-4xl font-extrabold tracking-tight text-[#002b5c]";
  const sectionSub = "text-gray-600 max-w-3xl mx-auto mt-2 text-base md:text-lg";

  const values = [
    {
      icon: <FaHandshake className="text-3xl text-[#e31c3d]" />,
      title: "Integrity First",
      text:
        "We recommend plans as if they were for our own familiesm we are transparent, unbiased, and compliant.",
    },
    {
      icon: <FaShieldAlt className="text-3xl text-[#e31c3d]" />,
      title: "Clarity & Guidance",
      text:
        "Insurance is complex. We make it simple with plain-English explanations and step-by-step help.",
    },
    {
      icon: <FaHeart className="text-3xl text-[#e31c3d]" />,
      title: "People Before Policies",
      text:
        "Every conversation starts with your needs, budget, and doctors, then coverage follows.",
    },
    {
      icon: <FaChartLine className="text-3xl text-[#e31c3d]" />,
      title: "Results You Feel",
      text:
        "Lower out-of-pocket costs, better benefits, and local support you can actually reach.",
    },
  ];

  const team = [
    {
      name: "Ruth Acosta",
      role: "Partner, Director of Operations",
      photo: "/images/team/RuthAcosta.jpg",
      location: "New York City, NY",
    },
    {
      name: "Alvaro Gutierrez",
      role: "Partner, Director of Broker Relations",
      photo: "/images/team/AlvaroGutierrez.jpg",
      location: "New York City, NY",
    }
  ];

  return (
    <div className="overflow-y-auto scrollbar-hide font-sans text-[#002b5c] bg-white">
      <Helmet>
        <title>About Us | Your Choice Associates</title>
        <meta
          name="description"
          content="Learn how Your Choice Associates helps families and brokers with clear, results-driven Medicare and Life Insurance guidance."
        />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="relative">
        <img
          src="/images/hero.png"
          alt="Clients and brokers smiling outdoors"
          className="w-full h-[420px] md:h-[520px] object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#002b5c]/80" />

        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-6">
            <div className="max-w-5xl mx-auto text-center text-white">
              <motion.h1
                {...fadeUp(0)}
                className="text-4xl md:text-6xl font-extrabold leading-tight"
              >
                About Your Choice Associates
              </motion.h1>
              <motion.p
                {...fadeUp(0.12)}
                className="mt-4 text-lg md:text-xl text-white/90 max-w-3xl mx-auto"
              >
                We help people feel confident about their Medicare and Life
                Insurance decisions—through clarity, integrity, and
                human-first support.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-white py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Families Served", value: "10,000+", icon: <FaUsers /> },
            { label: "Licensed Brokers", value: "30+", icon: <FaHandshake /> },
            { label: "Trusted Carriers", value: "20+", icon: <FaShieldAlt /> },
            { label: "States Supported", value: "50+", icon: <FaMapMarkerAlt /> },
          ].map((s, i) => (
            <motion.div
              key={i}
              {...fadeUp(0.05 * i)}
              className="rounded-2xl border border-[#002b5c]/10 bg-white p-6 text-center shadow-sm"
            >
              <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-[#002b5c]/10 flex items-center justify-center">
                <span className="text-[#002b5c]">{s.icon}</span>
              </div>
              <div className="text-3xl font-extrabold">{s.value}</div>
              <div className="text-sm text-gray-600 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STORY + MISSION */}
      <section className="bg-[#f9fafb] py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <motion.div {...fadeUp(0)}>
            <img
              src="/images/about-team.jpg"
              onError={(e) => (e.currentTarget.src = "/images/hero.png")}
              alt="Our team working with clients"
              className="rounded-2xl shadow-lg border border-[#002b5c]/10 object-cover w-full h-[320px] md:h-[420px]"
            />
          </motion.div>

          <motion.div {...fadeUp(0.1)}>
            <h2 className={sectionTitle}>Our Story</h2>
            <p className={sectionSub + " !mt-4"}>
              Your Choice Associates began with a simple idea: the best policy
              is the one you fully understand. Since day one, we've invested in
              broker training, vetted carrier partnerships, and tools that make
              it easy to compare benefits, so you can decide with confidence.
            </p>

            <div className="mt-8 rounded-2xl bg-white border border-[#002b5c]/10 p-6 shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#002b5c]">
                Our Mission
              </h3>
              <p className="text-gray-700 mt-2">
                Help brokers thrive and ensure families are protected, through
                clarity, integrity, and results-driven support in every policy
                delivered.
              </p>
              <ul className="mt-4 space-y-2 text-gray-700 list-disc list-inside">
                <li>Compare plans clearly, side-by-side.</li>
                <li>Match coverage to doctors, prescriptions, and budget.</li>
                <li>Offer lifetime service, not just enrollments.</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white py-24 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2 {...fadeUp(0)} className={sectionTitle}>
            What We Stand For
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className={sectionSub}>
            These core values guide how we show up for families and support our
            broker partners every day.
          </motion.p>

          <div className="mt-12 grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <motion.div
                key={i}
                {...fadeUp(0.05 * i)}
                className="bg-white border border-[#002b5c]/10 rounded-2xl p-8 shadow-sm hover:shadow-xl transition"
              >
                <div className="mb-4">{v.icon}</div>
                <h3 className="text-xl font-extrabold text-[#002b5c]">
                  {v.title}
                </h3>
                <p className="text-gray-600 mt-3">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-[#f9fafb] py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...fadeUp(0)} className={sectionTitle + " text-center"}>
            Milestones
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className={sectionSub + " text-center"}>
            How we grew—one conversation at a time.
          </motion.p>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            {[
              {
                year: "2005",
                title: "Founded",
                text:
                  "Started with a mission to simplify Medicare choices in our local community.",
              },
              {
                year: "2010",
                title: "Carrier Partnerships",
                text:
                  "Signed with top-rated carriers and expanded plan options for clients.",
              },
              {
                year: "2021",
                title: "10,000+ Families",
                text:
                  "Reached a major milestone—continuing our commitment to personal guidance.",
              },
              {
                year: "2025",
                title: "Broker Network",
                text:
                  "Launched our broker's website and scaled service to multiple states.",
              },
            ].map((t, i) => (
              <motion.div
                key={i}
                {...fadeUp(0.05 * i)}
                className="relative pl-8 border-l-2 border-[#002b5c]/15"
              >
                <span className="absolute -left-3 top-1 h-6 w-6 rounded-full bg-[#e31c3d]" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#e31c3d]">
                  {t.year}
                </h3>
                <div className="mt-1 text-xl font-extrabold text-[#002b5c]">
                  {t.title}
                </div>
                <p className="text-gray-700 mt-2">{t.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="bg-white py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...fadeUp(0)} className={sectionTitle + " text-center"}>
            Leadership
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className={sectionSub + " text-center"}>
            Experienced, approachable, and here to help.
          </motion.p>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {team.map((p, i) => (
              <motion.article
                key={i}
                {...fadeUp(0.05 * i)}
                className="bg-white border border-[#002b5c]/10 rounded-2xl p-8 text-center shadow-sm hover:shadow-xl transition"
              >
                <img
                  src={p.photo}
                  onError={(e) => (e.currentTarget.src = "/images/placeholder-avatar.png")}
                  alt={p.name}
                  className="mx-auto h-24 w-24 rounded-full object-cover ring-2 ring-[#002b5c]/15"
                />
                <h3 className="mt-4 text-lg font-bold">{p.name}</h3>
                <p className="text-[#002b5c]/80 font-medium">{p.role}</p>
                <p className="mt-1 text-sm text-gray-600 flex items-center justify-center gap-1">
                  <FaMapMarkerAlt className="text-[#e31c3d]" /> {p.location}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#e31c3d]/5 py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h2
            {...fadeUp(0)}
            className="text-3xl md:text-4xl font-extrabold text-[#002b5c]"
          >
            Ready to talk to a licensed broker?
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className="text-gray-700 mt-3">
            We’ll help you compare plans, confirm your doctors, and find
            benefits that fit—at no cost to you.
          </motion.p>
          <motion.div {...fadeUp(0.2)} className="mt-8 flex justify-center gap-4">
            <Link
              to="/plans"
              className="bg-[#002b5c] text-white px-6 py-3 rounded-full font-semibold shadow-md hover:bg-[#001b35] transition"
            >
              Browse Plans
            </Link>
            <Link
              to="/brokers"
              className="bg-white text-[#002b5c] border border-[#002b5c]/20 px-6 py-3 rounded-full font-semibold hover:bg-[#002b5c]/5 transition"
            >
              Meet Our Brokers
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
